import { CONTACT_EMAIL, CONTACT_PHONE, FORMSUBMIT_ENDPOINT } from './config';

interface Inquiry {
  name: string;
  email: string;
  projectType: string;
  message: string;
}

/** Shape of the JSON FormSubmit returns from its /ajax/ endpoint. */
interface FormSubmitResponse {
  success?: boolean | string;
  message?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_MESSAGE_LENGTH = 10;

function need<T extends Element>(root: ParentNode, selector: string): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`contact form: missing element "${selector}"`);
  return el;
}

function autoresponse({ name, projectType, message }: Inquiry): string {
  return [
    `Hi ${name},`,
    '',
    'Thanks for reaching out to Logika — your message has been received.',
    '',
    `Project type: ${projectType}`,
    '',
    'Your message:',
    message,
    '',
    'We read every inquiry personally and will get back to you with a proposal',
    'and an initial quote within 24 hours.',
    '',
    '— Logika',
    `${CONTACT_EMAIL} · ${CONTACT_PHONE}`,
    'logika.codes',
  ].join('\n');
}

async function send(inquiry: Inquiry): Promise<void> {
  const response = await fetch(FORMSUBMIT_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      name: inquiry.name,
      email: inquiry.email,
      'project type': inquiry.projectType,
      message: inquiry.message,
      _subject: `New inquiry — ${inquiry.projectType} — ${inquiry.name}`,
      _template: 'table',
      _captcha: 'false',
      // Confirmation mailed back to whoever submitted the form.
      _autoresponse: autoresponse(inquiry),
    }),
  });

  let payload: FormSubmitResponse = {};
  try {
    payload = (await response.json()) as FormSubmitResponse;
  } catch {
    // Non-JSON body — fall through to the status check below.
  }

  if (!response.ok || String(payload.success) !== 'true') {
    throw new Error(payload.message ?? 'The message could not be sent.');
  }
}

export function initContactForm(): void {
  const form = document.querySelector<HTMLFormElement>('#contact-form');
  if (!form) return;

  const nameInput = need<HTMLInputElement>(form, '#cf-name');
  const emailInput = need<HTMLInputElement>(form, '#cf-email');
  const messageInput = need<HTMLTextAreaElement>(form, '#cf-message');
  const honeyInput = need<HTMLInputElement>(form, '[name="_honey"]');
  const submitButton = need<HTMLButtonElement>(form, '#cf-submit');
  const statusBox = need<HTMLParagraphElement>(form, '#cf-status');

  const card = form.parentElement as HTMLElement;
  const sentPanel = need<HTMLDivElement>(card, '#cf-sent');
  const sentText = need<HTMLParagraphElement>(card, '#cf-sent-text');
  const againButton = need<HTMLButtonElement>(card, '#cf-again');

  const fields: Array<HTMLInputElement | HTMLTextAreaElement> = [
    nameInput,
    emailInput,
    messageInput,
  ];

  const submitLabel = submitButton.textContent ?? 'Send inquiry';

  function showStatus(text: string): void {
    statusBox.textContent = text;
    statusBox.hidden = false;
  }

  function clearStatus(): void {
    statusBox.hidden = true;
    statusBox.textContent = '';
  }

  function clearInvalid(): void {
    fields.forEach((field) => field.classList.remove('is-invalid'));
  }

  fields.forEach((field) => {
    field.addEventListener('input', () => field.classList.remove('is-invalid'));
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearStatus();
    clearInvalid();

    const inquiry: Inquiry = {
      name: nameInput.value.trim(),
      email: emailInput.value.trim(),
      message: messageInput.value.trim(),
      projectType:
        form.querySelector<HTMLInputElement>('input[name="project_type"]:checked')?.value ?? 'Web',
    };

    const invalid: Array<HTMLInputElement | HTMLTextAreaElement> = [];
    if (inquiry.name.length < 2) invalid.push(nameInput);
    if (!EMAIL_RE.test(inquiry.email)) invalid.push(emailInput);
    if (inquiry.message.length < MIN_MESSAGE_LENGTH) invalid.push(messageInput);

    if (invalid.length > 0) {
      invalid.forEach((field) => field.classList.add('is-invalid'));
      showStatus(
        `Please enter your name, a valid email address and a message of at least ${MIN_MESSAGE_LENGTH} characters.`,
      );
      invalid[0]?.focus();
      return;
    }

    // Bots fill hidden fields; humans never see this one.
    if (honeyInput.value !== '') return;

    submitButton.disabled = true;
    submitButton.textContent = 'Sending…';

    try {
      await send(inquiry);

      sentText.replaceChildren(
        document.createTextNode(
          `Thanks, ${inquiry.name} — your inquiry has landed in our inbox. A confirmation is on its way to `,
        ),
        Object.assign(document.createElement('strong'), { textContent: inquiry.email }),
        document.createTextNode('. We reply within 24 hours.'),
      );

      form.hidden = true;
      sentPanel.hidden = false;
      sentPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'The message could not be sent.';
      showStatus(`${reason} You can also email us directly at ${CONTACT_EMAIL}.`);
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = submitLabel;
    }
  });

  againButton.addEventListener('click', () => {
    form.reset();
    clearStatus();
    clearInvalid();
    sentPanel.hidden = true;
    form.hidden = false;
    nameInput.focus();
  });
}
